import * as React from 'react';
import cn from 'classnames';

import {
  LogoIcon,
  TelegramOriginalIcon,
  VKBaseIcon,
  VcRuIcon
} from 'shared/newComponents/icons';
import { IconSize } from 'shared/entities/components/Icon';
import {
  LINK_TO_BLOG,
  LINK_TO_TG,
  LINK_TO_VC_RU,
  LINK_TO_VK,
  VC_RU_LINK
} from 'shared/entities/app';
import { ComponentType } from 'shared/entities/components/Component';
import { AnalyticsEvent } from 'shared/entities/analytics';
import Reference from 'shared/newComponents/Reference';
import { useAnalyticsStore } from 'stores/index';
import TypedTransComponent from 'shared/components/TypedTransComponent';
import ExternalLink from 'shared/components/ExternalLink';

import './Accounts.modules.scss';

type Props = {
  size?: IconSize;
  className?: string;
};

const Accounts: React.FC<Props> = ({ size = IconSize.XL, className }) => {
  const analyticsStore = useAnalyticsStore();

  const handleVkClick = React.useCallback(() => {
    analyticsStore.sendEvent(AnalyticsEvent.linkToVkClick);
  }, []);

  const handleTgClick = React.useCallback(() => {
    analyticsStore.sendEvent(AnalyticsEvent.linkToTgClick);
  }, []);

  const handleSmartbotClick = React.useCallback(() => {
    analyticsStore.sendEvent(AnalyticsEvent.linkToSmartbotBlogClick);
  }, []);

  const handleVcRuClick = React.useCallback(() => {
    analyticsStore.sendEvent(AnalyticsEvent.linkToVcRuClick);
  }, []);

  return (
    <div styleName="accounts" className={className}>
      <Reference
        trigger={
          <a
            rel="noreferrer noopener"
            target="_blank"
            styleName={cn('accounts__item', 'accounts__item_logo')}
            href={LINK_TO_BLOG}
            onClick={handleSmartbotClick}
          >
            <LogoIcon />
          </a>
        }
        title={(t) =>
          t('SocialNetworks.blog.title', {
            ns: 'newComponents'
          })
        }
        description={(t) =>
          t('SocialNetworks.blog.description', {
            ns: 'newComponents'
          })
        }
      />
      <Reference
        trigger={
          <div styleName={cn('accounts__item', 'accounts__item_vk')}>
            <VKBaseIcon
              iconSize={size}
              action={{
                type: ComponentType.a,
                target: '_blank',
                href: LINK_TO_VK,
                onClick: handleVkClick
              }}
            />
          </div>
        }
        title={(t) =>
          t('SocialNetworks.vk.title', {
            ns: 'newComponents'
          })
        }
        description={(t) =>
          t('SocialNetworks.vk.description', {
            ns: 'newComponents'
          })
        }
      />
      <Reference
        trigger={
          <div styleName={cn('accounts__item', 'accounts__item_vc')}>
            <VcRuIcon
              iconSize={IconSize.XM}
              action={{
                type: ComponentType.a,
                target: '_blank',
                href: LINK_TO_VC_RU,
                onClick: handleVcRuClick
              }}
            />
          </div>
        }
        title={
          <TypedTransComponent
            i18nKey="SocialNetworks.vc.title"
            ns="newComponents"
          >
            Мы на{' '}
            <ExternalLink
              className="link-new link-new_inherit"
              href={VC_RU_LINK}
            >
              vc.ru
            </ExternalLink>
          </TypedTransComponent>
        }
        description={(t) =>
          t('SocialNetworks.vc.description', {
            ns: 'newComponents'
          })
        }
      />
      <Reference
        trigger={
          <div styleName={cn('accounts__item', 'accounts__item_tg')}>
            <TelegramOriginalIcon
              iconSize={size}
              action={{
                type: ComponentType.a,
                target: '_blank',
                href: LINK_TO_TG,
                onClick: handleTgClick
              }}
            />
          </div>
        }
        title={(t) =>
          t('SocialNetworks.telegram.title', {
            ns: 'newComponents'
          })
        }
        description={(t) =>
          t('SocialNetworks.telegram.description', {
            ns: 'newComponents'
          })
        }
      />
    </div>
  );
};

export default React.memo(Accounts);
